// import Datepicker, {DatepickerType} from 'react-tailwindcss-datepicker';
import {Popover, PopoverContent, PopoverTrigger} from '@/shadcn_components/ui/popover';
import {cn} from '@/lib/tailwind-utils';
import {CalendarIcon, XMarkIcon} from '@heroicons/react/24/outline';
import {Calendar, CalendarProps} from '@/shadcn_components/ui/calendar';
import {ReactNode, useEffect, useRef, useState} from 'react';
import formatter from '@/lib/formatter';
import useDialogState from '@/hooks/useDialogState';
import {Button} from '@/shadcn_components/ui/button';
import {Label} from '@/shadcn_components/ui/label';
import clsx from 'clsx';
import HelperText from './shared/HelperText';
import ErrorText from './shared/ErrorText';
import {InputProps, useInput} from '@/hooks/useInput';
import {Input} from '@/shadcn_components/ui/input';
import IMask from 'imask';

export type DatePickerInputProps = InputProps &
  Partial<React.ComponentProps<'div'>> & {
    readOnly?: boolean;
    formatDate?: (value) => string;
    calendar?: Partial<Omit<CalendarProps, 'onSelect' | 'date'>>;
    renderValue?: (value) => ReactNode;
    dismissOnSelect?: boolean;
    popoverProps?: Partial<React.ComponentProps<typeof Popover>>;
  };

const defaultFormatDate = formatter.formatDate;

const defaultParseDate = formatter.parseDate;

const DatepickerInput = (props: DatePickerInputProps) => {
  const {
    popoverProps,
    className,
    dismissOnSelect = true,
    // formatDate = formatter.formatDate,
    calendar,
    format: formatDate = defaultFormatDate, // Formats for display
    parse: parseDate = defaultParseDate, // Parses input to Date
    hidden,
    readOnly,
    disabled,
  } = props;

  const inputRef = useRef<any>(null);
  const maskRef = useRef<any>(null);

  const {
    field: {value, onChange},
    fieldState,
  } = useInput(props);
  const {mode = 'single'} = calendar || {};

  function format(value) {
    if (mode === 'single') return formatDate(value);
    if (mode === 'range' && value?.from && value?.to) {
      return `${formatDate(value.from)} -> ${formatDate(value.to)}`;
    }
    return '';
  }
  function parse(str) {
    if (mode === 'single') {
      return parseDate(str);
    }
    const [from, to] = str.split(' -> ').map(parseDate);
    return {from, to};
  }

  const [month, setMonth] = useState(new Date());

  const [selectValue, setSelectedValue] = useState(value);

  useEffect(() => {
    if (inputRef.current) {
      maskRef.current = IMask(inputRef.current, {
        mask: Date,
        pattern: mode == 'single' ? '`d{/}`m{/}`Y' : '`d{/}`m{/}`Y -> `d{/}`m{/}`Y',
        lazy: false,
        blocks: {
          d: {
            mask: IMask.MaskedRange,
            from: 1,
            to: 31,
            maxLength: 2,
            autofix: 'pad',
          },
          m: {
            mask: IMask.MaskedRange,
            from: 1,
            to: 12,
            maxLength: 2,
            autofix: 'pad',
          },
          Y: {
            mask: IMask.MaskedRange,
            from: 1900,
            to: 2100,
            maxLength: 4,
          },
        },
        format,
        parse,
      });

      // Handle changes by imask
      maskRef.current.on('accept', () => {
        const maskedValue = maskRef.current.value;

        // Store the formatted date or empty if invalid
        const parsedDate = parse(maskedValue);
        // console.log('parsedDate', parsedDate);
        if (mode === 'single' && !isNaN(parsedDate.getTime())) {
          // setDate(datefns.format(parsedDate, 'dd/MM/yyyy'));
          onChange(parsedDate);
          setSelectedValue(parsedDate);
          setMonth(parsedDate);
        }

        if (
          mode === 'range' &&
          !isNaN(parsedDate.from.getTime()) &&
          !isNaN(parsedDate.to.getTime())
        ) {
          // setDate(datefns.format(parsedDate, 'dd/MM/yyyy'));
          onChange(parsedDate);
          setSelectedValue(parsedDate);
          setMonth(parsedDate.from);
        }
      });

      return () => {
        maskRef.current?.off('accept');
        maskRef.current?.destroy();
      };
    }
  }, []);

  useEffect(() => {
    if (maskRef.current && value) {
      maskRef.current.value = format(value);
    }
  }, [value]);

  const popupState = useDialogState();

  return (
    <div className={clsx('space-y-1', className, {hidden: hidden})}>
      <Label
      // className='text-lg'
      >
        {props.label}
      </Label>
      <div className="w-full relative flex items-center p-0">
        <Input
          type="text"
          placeholder={props.label || 'Nhập ngày tháng ...'}
          className={cn('h-8 pr-8', className)}
          value={maskRef.current?.value || ''}
          ref={inputRef}
          onChange={e => {
            e;
          }}
          readOnly={readOnly}
          disabled={disabled}
        />
        {!hidden && !readOnly && !disabled && (
          <>
            <Popover
              open={popupState.open}
              onOpenChange={popupState.setOpen}
              modal
              {...popoverProps}>
              <PopoverTrigger asChild>
                <CalendarIcon
                  className={cn(
                    'hover:text-primary absolute right-8 top-0 h-full w-4 text-muted-foreground',
                    {hidden: hidden || readOnly || disabled},
                  )}
                />
              </PopoverTrigger>
              <PopoverContent className="w-auto h-auto" align="start" side="bottom">
                <Calendar
                  mode={mode}
                  selected={selectValue}
                  onSelect={v => {
                    setSelectedValue(v);
                    // dismiss?
                    if (mode === 'single' && dismissOnSelect) {
                      onChange(v);
                      if (maskRef.current) maskRef.current.value = format(v);
                      popupState.closeDialog();
                    }
                  }}
                  month={month}
                  onMonthChange={setMonth}
                  {...calendar}
                />
                {(mode != 'single' || !dismissOnSelect) && (
                  <div className="flex flex-row-reverse">
                    <Button
                      // disabled={!value}
                      onClick={() => {
                        onChange(selectValue);
                        if (maskRef.current) maskRef.current.value = format(selectValue);
                        popupState.closeDialog();
                      }}>
                      Chọn ngày
                    </Button>
                  </div>
                )}
              </PopoverContent>
            </Popover>
            {(value || maskRef.current?.value) && (
              <XMarkIcon
                className={cn(
                  'hover:text-primary absolute right-2 top-0 h-full w-4 text-muted-foreground cursor-pointer',
                  {hidden: hidden || readOnly || disabled},
                )}
                onClick={() => {
                  onChange(null);
                  setSelectedValue(null);
                  if (maskRef.current) maskRef.current.value = '';
                }}
              />
            )}
          </>
        )}
      </div>

      <HelperText helperText={props.helperText} />
      <ErrorText {...fieldState} />
    </div>
  );
};

export default DatepickerInput;
